import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { withDayTimeZone } from '@/lib/utils'
import { dealKeys } from '@/hooks/api/deal-keys'
import { dashboardKeys } from '@/hooks/api/dashboard'

/**
 * Typed data-access layer for the `/tasks` endpoints.
 *
 * Centralises the query keys, URL building, response envelope, and cache
 * invalidation that were previously hand-rolled inline across TasksPage and
 * TaskModal, DealDetailPage and DashboardPage. The list key is preserved
 * verbatim — same positional shape `['tasks', tab, page, status, priority,
 * dueDateFrom, dueDateTo, branchId, onlyMine, search]` — so the ad-hoc
 * `invalidateQueries({ queryKey: ['tasks'] })` calls keep matching.
 */

export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

interface TaskUserRef {
  id: string
  firstName: string
  lastName: string
}

/** Row shape returned by `GET /tasks`. */
export interface TaskListItem {
  id: string
  title: string
  description: string | null
  status: TaskStatus
  priority: TaskPriority
  dueAt: string | null
  completedAt: string | null
  createdAt: string
  assignee: TaskUserRef | null
  createdBy: TaskUserRef | null
  deal: { id: string; title: string } | null
  propertyId: number | null
  contact: { id: string; firstName: string; lastName: string; phone: string | null } | null
  contactName: string | null
  contactPhone: string | null
}

export interface TasksResult {
  data: TaskListItem[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

/** Fully-resolved list filters — the page resolves RBAC/URL state, the hook
 * just serialises. `search` mirrors `searchParams.get('search')` so it can be
 * `null`. */
export interface TaskListFilters {
  tab: 'active' | 'archive'
  page: number
  status?: string
  priority?: string
  dueDateFrom?: string
  dueDateTo?: string
  branchId?: string
  onlyMine: boolean
  search: string | null
}

export const taskKeys = {
  /** Prefix matching every task list query — use for broad invalidation. */
  all: ['tasks'] as const,
  list: (f: TaskListFilters) =>
    [
      'tasks',
      f.tab,
      f.page,
      f.status ?? '',
      f.priority ?? '',
      f.dueDateFrom ?? '',
      f.dueDateTo ?? '',
      f.branchId ?? '',
      f.onlyMine,
      f.search,
    ] as const,
}

export function useTasks(filters: TaskListFilters) {
  return useQuery<TasksResult>({
    queryKey: taskKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('tab', filters.tab)
      params.set('page', String(filters.page))
      params.set('limit', '25')
      if (filters.status) params.set('status', filters.status)
      if (filters.priority) params.set('priority', filters.priority)
      if (filters.dueDateFrom) params.set('dueDateFrom', filters.dueDateFrom)
      if (filters.dueDateTo) params.set('dueDateTo', filters.dueDateTo)
      if (filters.branchId) params.set('branchId', filters.branchId)
      if (filters.onlyMine) params.set('onlyMine', 'true')
      if (filters.search) params.set('search', filters.search)
      const { data } = await api.get<TasksResult>(`/tasks?${withDayTimeZone(params)}`)
      return data
    },
  })
}

/* ------------------------------------------------------------------ *
 * Mutations
 *
 * Each hook owns the URL + envelope + cache invalidation. Page-specific
 * reactions (toasts, modal close, form resets) stay at the call site via
 * `mutate(vars, { onSuccess, onError })`.
 * ------------------------------------------------------------------ */

/**
 * Úloha sa zobrazuje v zoznamoch a kalendári (['tasks', …]), v detaile obchodu
 * (deal.tasks) a na dashboarde (myTasks) — po každej zmene obnoviť všetky.
 * Dashboard sa len označí ako zastaraný (načíta sa pri ďalšom otvorení): na ňom
 * samom by okamžitý refetch skryl odškrtnutú úlohu ešte pred koncom animácie,
 * DashboardPage ho po nej obnoví sám.
 */
function invalidateTaskViews(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: taskKeys.all })
  qc.invalidateQueries({ queryKey: dealKeys.details })
  qc.invalidateQueries({ queryKey: dashboardKeys.stats, refetchType: 'none' })
}

/** Mark a task as completed (PATCH /tasks/{id}/complete). */
export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.patch(`/tasks/${id}/complete`),
    onSuccess: () => invalidateTaskViews(qc),
  })
}

/** Patch arbitrary task fields — status/cancel/update (PATCH /tasks/{id}). */
export function useUpdateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patch(`/tasks/${id}`, payload),
    onSuccess: () => invalidateTaskViews(qc),
  })
}

/** Create a task (POST /tasks). */
export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post('/tasks', payload),
    onSuccess: () => invalidateTaskViews(qc),
  })
}
